import { useLocation, useRoute } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canSeeBilling } from "../../auth/roleView";
import { TabPanel, Tabs, type TabItem } from "../../components/Tabs";
import { AccountPage } from "../account/AccountPage";
import { SSOConfigPage } from "../admin/sso/SSOConfigPage";
import { UsersPage } from "../admin/users/UsersPage";
import { BillingPage } from "../billing/BillingPage";
import "./settings.css";

/**
 * Settings' home (IA slice 5, docs/product/ui-information-architecture.md §3: "Settings ←
 * users · SSO · billing · account"). Mounted at `/settings` (renamed from `/account` in IA
 * slice 6 — a clean pre-beta rename, no alias kept, see App.tsx) — the route every signed-in
 * role lands on for its own profile — with three admin-scoped sibling tabs. Every tab
 * mounts an existing, unchanged page component (A-5: one implementation of each area's
 * query/mutation logic, never a second copy for the tabbed context); this component only
 * owns which tab is active, the route it lives at, and which tabs a role can even see.
 *
 * Role-aware, HIDDEN not greyed (S-8 — this is UX only, the server enforces the real
 * decision on every request): Users and SSO are admin-only tabs (mirrors `AdminGate`,
 * used inside both pages too — belt-and-suspenders); Billing shows for admin and
 * billing_admin (`canSeeBilling`). A `billing_admin` never reaches this page via the nav
 * (its one pinned link is "Billing", straight to `/billing` — `NavSidebar`), but if it
 * deep-links `/settings` directly, it still sees only the tabs it can act in, same as
 * every other role here — `/billing` itself is untouched by this page and keeps working
 * exactly as it did before (`App.tsx`).
 */
export function SettingsPage() {
  const { session } = useAuth();
  const [, navigate] = useLocation();
  const [matchUsers] = useRoute("/settings/users");
  const [matchSSO] = useRoute("/settings/sso");
  const [matchBilling] = useRoute("/settings/billing");
  const activeTab = matchUsers ? "users" : matchSSO ? "sso" : matchBilling ? "billing" : "account";

  const isAdmin = session?.role === "admin";
  const showBilling = canSeeBilling(session?.role);

  const tabItems: TabItem[] = [
    { id: "account", label: "Account" },
    ...(isAdmin ? [{ id: "users", label: "Users" }] : []),
    ...(isAdmin ? [{ id: "sso", label: "SSO" }] : []),
    ...(showBilling ? [{ id: "billing", label: "Billing" }] : []),
  ];

  // A role that can't see the tab it's currently on (e.g. a stale deep link
  // held by a role that lost admin) falls back to Account, the one tab every
  // signed-in session can always see.
  const resolvedActiveTab = tabItems.some((t) => t.id === activeTab) ? activeTab : "account";

  function handleTabChange(id: string) {
    navigate(id === "account" ? "/settings" : `/settings/${id}`);
  }

  return (
    <div className="settings-page">
      <h1 className="visually-hidden">Settings</h1>
      <div className="settings-page__tabs">
        <Tabs items={tabItems} activeId={resolvedActiveTab} onChange={handleTabChange} ariaLabel="Settings views" />
      </div>

      <TabPanel id="account" activeId={resolvedActiveTab}>
        <AccountPage />
      </TabPanel>
      {isAdmin && (
        <TabPanel id="users" activeId={resolvedActiveTab}>
          <UsersPage />
        </TabPanel>
      )}
      {isAdmin && (
        <TabPanel id="sso" activeId={resolvedActiveTab}>
          <SSOConfigPage />
        </TabPanel>
      )}
      {showBilling && (
        <TabPanel id="billing" activeId={resolvedActiveTab}>
          <BillingPage />
        </TabPanel>
      )}
    </div>
  );
}
